import React from 'react';
import { CTASection } from '../components/sections';
import { 
  Compass,
  Target,
  Users,
  FileText,
  Settings,
  Shield,
  CheckCircle2,
  TrendingUp,
  ClipboardList,
  Handshake
} from 'lucide-react';

const SERVICES = [
  { id: 'deployment-strategy', icon: Target, title: 'Deployment Strategy', description: 'Strategic planning for network rollouts, ensuring efficient resource allocation and timeline management for successful deployments.' },
  { id: 'vendor-evaluation', icon: Users, title: 'Vendor and Contractor Evaluation', description: 'Independent assessment of vendors and contractors to help you select the right partners for your infrastructure projects.' },
  { id: 'quality-assurance', icon: Shield, title: 'Quality Assurance Frameworks', description: 'Development and implementation of QA processes to maintain consistent standards across all field operations.' },
  { id: 'field-optimization', icon: Settings, title: 'Field Operations Optimization', description: 'Analysis and improvement of field workflows to increase efficiency, reduce costs, and improve safety outcomes.' },
  { id: 'documentation', icon: FileText, title: 'Technical Documentation & SOP Creation', description: 'Professional documentation services including standard operating procedures, work instructions, and technical manuals.' }
];

const APPROACH = [
  { step: '01', title: 'Discovery', description: 'We begin by understanding your current operations, challenges, and objectives through detailed consultations.' },
  { step: '02', title: 'Analysis', description: 'Our team analyzes your processes, identifies gaps, and benchmarks against industry best practices.' },
  { step: '03', title: 'Strategy', description: 'We develop tailored recommendations and actionable strategies aligned with your business goals.' },
  { step: '04', title: 'Implementation', description: 'We support the execution of recommendations with hands-on guidance and progress monitoring.' }
];

const WHY_CHOOSE_US = [
  { id: 'expertise', text: 'Deep industry expertise in telecommunications infrastructure' },
  { id: 'experience', text: 'Practical experience from years of field operations' },
  { id: 'neutral', text: 'Vendor-neutral recommendations focused on your success' },
  { id: 'track-record', text: 'Proven track record of optimizing network deployments' },
  { id: 'support', text: 'Ongoing support throughout implementation' }
];

const STATS = [
  { icon: TrendingUp, value: '30%', label: 'Efficiency Gains' },
  { icon: ClipboardList, value: '50+', label: 'Projects Advised' },
  { icon: Handshake, value: '100%', label: 'Client Focused' },
  { icon: Shield, value: '15+', label: 'Years Experience' }
];

const AdvisoryHero = () => (
  <section className="relative py-20 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f1d32] to-[#162544]" />
    <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-[#8b5cf6]/10 rounded-full blur-3xl" />
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#8b5cf6]/20 flex items-center justify-center">
            <Compass className="w-6 h-6 text-[#8b5cf6]" />
          </div>
          <span className="text-[#8b5cf6] font-medium">Advisory Services</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#f1f5f9] mb-6 animate-fade-in-up">
          Strategic Guidance for Network Success
        </h1>
        <p className="text-xl text-[#94a3b8] leading-relaxed animate-fade-in-up delay-100">
          Expert advisory services to optimize your network deployment and operations. 
          We bring industry experience and technical depth to help you make informed decisions.
        </p>
      </div>
    </div>
  </section>
);

const ServicesSection = () => (
  <section className="py-20 bg-[#0f1d32]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#f1f5f9] mb-4">Advisory Services</h2>
        <p className="text-[#94a3b8] max-w-2xl mx-auto">
          Comprehensive guidance across all aspects of telecommunications infrastructure
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((service) => (
          <div 
            key={service.id}
            className="bg-[#162544] rounded-2xl p-8 border border-[#1e3a5f]/50 card-hover group"
          >
            <div className="w-14 h-14 rounded-xl bg-[#8b5cf6]/20 flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
              <service.icon className="w-7 h-7 text-[#8b5cf6]" />
            </div>
            <h3 className="text-xl font-bold text-[#f1f5f9] mb-3">{service.title}</h3>
            <p className="text-[#94a3b8] text-sm leading-relaxed">{service.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ApproachSection = () => (
  <section className="py-20 bg-[#0a1628]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#f1f5f9] mb-4">Our Approach</h2>
        <p className="text-[#94a3b8] max-w-2xl mx-auto">
          A structured methodology to deliver actionable insights and measurable results
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {APPROACH.map((phase) => (
          <div key={phase.step} className="relative">
            {phase.step !== '04' && (
              <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-[#8b5cf6]/50 to-transparent z-0" />
            )}
            
            <div className="relative z-10 text-center">
              <div className="w-24 h-24 rounded-2xl bg-[#162544] border border-[#8b5cf6]/30 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-[#8b5cf6]">{phase.step}</span>
              </div>
              <h3 className="text-xl font-bold text-[#f1f5f9] mb-3">{phase.title}</h3>
              <p className="text-sm text-[#94a3b8]">{phase.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyChooseUsSection = () => (
  <section className="py-20 bg-[#0f1d32]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row items-center gap-12">
        <div className="lg:w-1/2">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#f1f5f9] mb-6">Why Work With Us</h2>
          <p className="text-[#94a3b8] mb-8 leading-relaxed">
            Our advisory team combines hands-on field experience with strategic thinking 
            to deliver practical, implementable recommendations that drive real results.
          </p>
          <ul className="space-y-4">
            {WHY_CHOOSE_US.map((item) => (
              <li key={item.id} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8b5cf6] flex-shrink-0 mt-0.5" />
                <span className="text-[#f1f5f9]">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:w-1/2 flex justify-center">
          <div className="grid grid-cols-2 gap-4">
            {STATS.map((stat, index) => (
              <div key={index} className="bg-[#162544] rounded-xl p-6 border border-[#1e3a5f]/50 text-center">
                <stat.icon className="w-10 h-10 text-[#8b5cf6] mx-auto mb-3" />
                <div className="text-2xl font-bold text-[#f1f5f9]">{stat.value}</div>
                <div className="text-sm text-[#64748b]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Advisory = () => {
  return (
    <div className="min-h-screen bg-[#0a1628]">
      <AdvisoryHero />
      <ServicesSection />
      <ApproachSection />
      <WhyChooseUsSection />
      <CTASection
        title="Ready for Strategic Guidance?"
        description="Let's discuss how our advisory services can help optimize your network operations and achieve your business objectives."
        actionLabel="Schedule a Consultation"
        actionTo="/contact"
        accentColor="#8b5cf6"
      />
    </div>
  );
};

export default Advisory;
